import { createRoot } from "react-dom/client";
import { BIView } from "../cases/BIView";

export const initCaseView = () => {
  const container = document.getElementById("bi-details-section");

  if (container) {
    const root = createRoot(container);
    const dataset = container.dataset;
    const biState = dataset.biState;
    const biErrors = JSON.parse(dataset.biErrors);
    const biNoOonBenefitsError = JSON.parse(dataset.biNoOonBenefitsError);
    const pharmacyProviderInfo = JSON.parse(dataset.pharmacyProviderInfo);
    const secondaryPayorBenefitsData = JSON.parse(dataset.secondaryPayorBenefitsData);
    const secondaryRequestInsuranceData = JSON.parse(dataset.secondaryRequestInsuranceData);
    const cptCodesWithMultiplier = JSON.parse(dataset.cptCodes);
    const showSecondaryInsuranceResult = JSON.parse(dataset.showSecondaryInsuranceResult);
    const cptFeeScheduleByTest = JSON.parse(dataset.cptFeeScheduleByTestPricing);

    const testInfo = {
      name: dataset.testName,
      cptCodes: cptCodesWithMultiplier,
      icd10Codes: dataset.icd10Codes?.split(","),
      coverageType: dataset.insuranceCoverageType,
      labName: dataset.labName,
      orderId: dataset.orderId,
      dateOfService: dataset.insuranceDateOfService,
    };

    const practiceInfo = {
      name: dataset.providerName,
      practiceName: dataset.providerPracticeName,
      npi: dataset.providerNpi,
      enrolled: dataset.providerEnrolled,
      enrollmentRequired: dataset.providerEnrollmentRequired,
      clinicId: dataset.providerClinicId,
    };

    const otherInfo = {
      dateOfService: dataset.insuranceDateOfService,
      orderId: dataset.orderId,
      icdCodes: dataset.icd10Codes,
      collectionDate: dataset.insuranceCollectionDate,
    };

    const pharmacyPracticeInfo = {
      practiceName: dataset.labName,
      phone: pharmacyProviderInfo.phone,
      faxNumber: pharmacyProviderInfo.fax,
      addressInfoSnowflake: pharmacyProviderInfo.address,
      npi: pharmacyProviderInfo.provider_npi,
      siteService: pharmacyProviderInfo.site_of_service,
      coverageType: dataset.insuranceCoverageType,
      taxId: pharmacyProviderInfo.tax_id,
    };

    const insuranceInfo = {
      name: dataset.insuranceName,
      verifiedName: dataset.insuranceVerifiedName,
      coverageType: dataset.insuranceCoverageType,
      planType: dataset.insurancePlanType || null,
      memberId: dataset.insuranceMemberId,
      activeCoverage: dataset.insuranceActiveCoverage,
      fundingType: dataset.insuranceFundingType,
      effectiveDate: dataset.insuranceEffectiveDate,
      employerName: dataset.insuranceEmployerName,
      policyEndDate: dataset.insurancePolicyEndDate,
      payorPhone: dataset.insurancePayorPhone,
      benefitsVerified: dataset.insuranceBenefitsVerified,
      groupId: dataset.insuranceGroupId,
      planName: dataset.insurancePlanName,
      dateVerified: dataset.insuranceDateVerified,
      subscriberRelationship: dataset.insuranceSubscriberRelationship,
    };

    const secondaryInsuranceInfo = {
      verifiedName: secondaryRequestInsuranceData.secondary_verified_payor_name,
      memberId: secondaryRequestInsuranceData.secondary_insurance_member_id,
      coverageType: secondaryRequestInsuranceData.secondary_insurance_coverage_type,
      fundingType: secondaryRequestInsuranceData.secondary_insurance_funding_type,
      payorPhone: secondaryRequestInsuranceData.primary_contact_number,
      groupId: secondaryRequestInsuranceData.secondary_insurance_group_id,
      name: secondaryRequestInsuranceData.secondary_request_insurance_name,
      activeCoverage: secondaryRequestInsuranceData.secondary_insurance_active_coverage,
      subscriberRelationship: secondaryRequestInsuranceData.secondary_insurance_subscriber_relationship,
      planType: secondaryPayorBenefitsData.plan_type || null,
      effectiveDate: secondaryPayorBenefitsData.effective_date,
      employerName: secondaryPayorBenefitsData.employer_name,
      policyEndDate: secondaryPayorBenefitsData.effective_end_date,
      planName: secondaryPayorBenefitsData.plan_name,
      benefitsVerified: dataset.insuranceBenefitsVerified,
      dateVerified: dataset.insuranceDateVerified,
    };

    const biInfo = JSON.parse(dataset.biInfo);
    if (biInfo && dataset.productBenefits) {
      biInfo.productBenefits = JSON.parse(dataset.productBenefits);
    }
    if (biInfo && dataset.secondaryPayorBenefitsData) {
      biInfo.secondaryPayorBenefitsData = secondaryPayorBenefitsData;
    }
    if (biInfo) {
      biInfo.deductible_cents = dataset.deductibleCents;
      biInfo.moop_cents = dataset.moopCents;
      biInfo.cpt_codes_with_multiplier = cptCodesWithMultiplier;
    }

    const hasVerifiedPayor = JSON.parse(dataset.hasVerifiedPayor);
    const hasVerifiedTests = JSON.parse(dataset.hasVerifiedTests);
    const verifiedCptCodes = JSON.parse(dataset.paRequirementsAndApprovals);
    const serviceType = dataset.serviceType;

    const paRequirementsConfigured = dataset.paRequirementsConfigured === "true";

    const hidePolicyLink = dataset.hideMedicalPolicy === "true";
    const noMoopProvided = dataset.noMoopProvidedByPayor === "true";
    const priceComparisonEnabled = dataset.priceComparisonEnabled === "true";
    const payorShowPriceComparison = dataset.payorShowPriceComparison === "true";
    const casePublicId = dataset.caseId || "";

    const trizettoEnabled = dataset.trizettoEnabled === "true";
    const coveredChargesEnabled = dataset.showCoveredCharges === "true";
    const showPaApprovalPercentage = dataset.showPaApprovedPercentage === "true";

    const isPharmacyOwnedByClinic = dataset.isPharmacyOwnedByClinic === "true";
    const clinicPaSubmitRightEnabled = dataset.clinicPaSubmitRight === "true";

    const priorAuthorizationDetails = JSON.parse(dataset.priorAuthorizationDetails || "{}");

    const labOrClinicUser = JSON.parse(dataset.labOrClinicUser);
    const clinicTypePharmacy = JSON.parse(dataset.clinicTypePharmacy);

    root.render(
      <BIView
        test={testInfo}
        practice={practiceInfo}
        insurance={insuranceInfo}
        secondaryInsurance={secondaryInsuranceInfo}
        pharmacyPractice={pharmacyPracticeInfo}
        other={otherInfo}
        bi={biInfo}
        configured={paRequirementsConfigured}
        hidePolicyLink={hidePolicyLink}
        biState={biState}
        biErrors={biErrors}
        biNoOonBenefitsError={biNoOonBenefitsError}
        priceComparisonEnabled={priceComparisonEnabled}
        payorShowPriceComparison={payorShowPriceComparison}
        noMoopProvided={noMoopProvided}
        caseId={casePublicId}
        hasVerifiedPayor={hasVerifiedPayor}
        hasVerifiedTests={hasVerifiedTests}
        verifiedCptCodes={verifiedCptCodes}
        serviceType={serviceType}
        trizettoEnabled={trizettoEnabled}
        showPaApprovalPercentage={showPaApprovalPercentage}
        isPharmacyOwnedByClinic={isPharmacyOwnedByClinic}
        clinicPaSubmitRightEnabled={clinicPaSubmitRightEnabled}
        priorAuthorizationDetails={priorAuthorizationDetails}
        labOrClinicUser={labOrClinicUser}
        clinicTypePharmacy={clinicTypePharmacy}
        coveredChargesEnabled={coveredChargesEnabled}
        cptFeeScheduleByTest={cptFeeScheduleByTest}
        showSecondaryInsuranceResult={showSecondaryInsuranceResult}
      />
    );
  }
};
